import { FC, useRef } from 'react';
import * as React from 'react';
import {
  Formik,
  Field,
  Form as FormikForm,
  FormikErrors,
  FieldConfig,
  FieldProps,
  useFormikContext
} from 'formik';
import { FormattedMessage } from 'react-intl';
import {
  LoadableButton,
  Box,
  Label,
  Select,
  Input,
  H2,
  I18NSave,
  Text,
  CheckboxStateless,
  Flex,
  Alert
} from '../../../components';
import { useUpdateCurrentUserMutation } from '../../../lib/core/mutations';
import * as messages from '../messages';
import * as globalMessages from '../../../lib/messages';
import { CurrentUser, Gender } from '../../../core-types';
import { GraphQLErrors } from '../../../components/GraphQLErrors';
import { BoxProps, TextProps } from '@urbaninfrastructure/react-ui-kit';
import { useToasts } from '../../../components/Toasts';
import { postalCodeForm, emailForm } from '../../Onboarding/messages';
import * as Yup from 'yup';

type Props = {
  currentUser: CurrentUser;
};

const FormDescription: FC<{ id: string } & Omit<
  TextProps,
  'color' | 'as'
>> = props => {
  return <Text mute small {...props} />;
};

type FormikFieldProps<TData> = {
  name: keyof TData;
} & Omit<FieldConfig, 'name'>;

function FormikField<T>({ name, ...props }: FormikFieldProps<T>) {
  return <Field name={name} {...props} />;
}

const FormGroup: FC<BoxProps & {
  name: string;
  label?: React.ReactNode;
  render: (renderProps: {
    inputProps: { 'aria-describedby'?: string };
  }) => React.ReactNode;
  description?: React.ReactNode;
}> = ({ label, name, render, description, color, ...props }) => {
  const { errors, touched } = useFormikContext<Values>();
  const error = touched[name] && errors[name];
  const errorMessage = error && messages.userFormValidation[name];
  const inputProps = {};

  let descriptionId = `${name}_desc`;

  if (description) {
    inputProps['aria-describedby'] = descriptionId;
  }

  return (
    <Box mb={6} color={color as any} {...props}>
      {label && <Label htmlFor={name}>{label}</Label>}
      {render({ inputProps })}
      {errorMessage && (
        <Alert variant="error" mt={4}>
          <FormattedMessage {...errorMessage} />
        </Alert>
      )}
      {description && (
        <FormDescription id={descriptionId} mt="xxs">
          {description}
        </FormDescription>
      )}
    </Box>
  );
};

interface Values {
  gender: Gender | null;
  name: string;
  birthYear: string;
  email: string;
  postalCode: string;
  preferredLanguageCode: string;
  agreedToReceiveEmail: boolean;
  agreedToReceiveExternalPromotions: boolean;
}

const InnerUserForm = ({ currentUser }: Props) => {
  const firstInput = useRef<HTMLInputElement>(null);
  const { addToast } = useToasts();
  const [updateCurrentUser, { loading, error }] = useUpdateCurrentUserMutation({
    onCompleted: () => {
      addToast({
        text: <FormattedMessage {...messages.profileSection.profileUpdated} />,
        type: 'success'
      });
    }
  });
  const initialValues: Values = {
    gender: currentUser.gender,
    name: currentUser.name || '',
    birthYear: currentUser.birthYear || '',
    email: currentUser.email || '',
    postalCode: currentUser.postalCode || '',
    preferredLanguageCode: currentUser.preferredLanguageCode || '',
    agreedToReceiveEmail: currentUser.agreedToReceiveEmail || false,
    agreedToReceiveExternalPromotions:
      currentUser.agreedToReceiveExternalPromotions || false
  };

  const birthYearRegExp = /\b\d{4}\b/;

  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .email()
      .required(),
    birthYear: Yup.string().matches(birthYearRegExp)
  });

  function isFormValid({
    errors,
    dirty
  }: {
    errors: FormikErrors<Values>;
    dirty: boolean | undefined;
  }) {
    if (dirty) {
      if (Object.keys(errors).length === 0) {
        return true;
      } else {
        return false;
      }
    }
    return false;
  }

  return (
    <Formik<Values>
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={values => {
        updateCurrentUser({
          variables: {
            input: {
              gender: values.gender || null,
              name: values.name || null,
              birthYear: values.birthYear || null,
              email: values.email || null,
              postalCode: values.postalCode || null,
              preferredLanguageCode: values.preferredLanguageCode || null,
              agreedToReceiveEmail: values.agreedToReceiveEmail || false,
              agreedToReceiveExternalPromotions:
                values.agreedToReceiveExternalPromotions || false
            }
          }
        });
      }}
      validate={() => {
        const errors: FormikErrors<Values> = {};
        return errors;
      }}
    >
      {({ dirty, errors }) => {
        const disabled = loading;
        const birthYearAndGenderDescriptionId = 'birthYearAndGender_desc';
        return (
          <FormikForm id="UserForm">
            <H2>
              <FormattedMessage {...messages.profileSection.formHeading} />
            </H2>
            <Text as="p">
              <FormattedMessage {...messages.userFields.disclaimer} />
            </Text>
            {error && <GraphQLErrors error={error} />}
            <FormikField<Values> name="name">
              {(props: FieldProps<Values['name']>) => {
                return (
                  <FormGroup
                    name={props.field.name}
                    label={<FormattedMessage {...messages.userFields.name} />}
                    render={formGroupProps => (
                      <Input
                        id={props.field.name}
                        ref={firstInput}
                        autoComplete="name"
                        {...props.field}
                        {...formGroupProps.inputProps}
                      />
                    )}
                    description={
                      <FormattedMessage
                        {...messages.userFields.nameDescription}
                      />
                    }
                  />
                );
              }}
            </FormikField>
            <Field name="email">
              {(props: FieldProps<Values['email']>) => {
                return (
                  <FormGroup
                    name={props.field.name}
                    label={<FormattedMessage {...messages.userFields.email} />}
                    render={formGroupProps => (
                      <Input
                        id={props.field.name}
                        autoComplete="email"
                        type="email"
                        {...props.field}
                        {...formGroupProps.inputProps}
                      />
                    )}
                    description={
                      <FormattedMessage {...emailForm.description} />
                    }
                    {...props}
                  />
                );
              }}
            </Field>
            <Field name="agreedToReceiveEmail">
              {({
                field: { value, ...field },
                form
              }: FieldProps<Values['agreedToReceiveEmail']>) => {
                return (
                  <FormGroup
                    mb="xxs"
                    name={field.name}
                    render={() => {
                      return (
                        <CheckboxStateless
                          label={
                            <FormattedMessage
                              {...messages.userFields.agreedToReceiveEmail}
                            />
                          }
                          alternative
                          disabled={disabled}
                          checked={value}
                          onBlur={field.onBlur}
                          onChange={event => {
                            form.setFieldValue(
                              'agreedToReceiveEmail',
                              event.target.checked
                            );
                          }}
                        />
                      );
                    }}
                  />
                );
              }}
            </Field>
            <Field name="agreedToReceiveExternalPromotions">
              {({
                field: { value, ...field },
                form
              }: FieldProps<Values['agreedToReceiveExternalPromotions']>) => {
                return (
                  <FormGroup
                    name={field.name}
                    render={() => {
                      return (
                        <CheckboxStateless
                          label={
                            <FormattedMessage
                              {...messages.userFields
                                .agreedToReceiveExternalPromotions}
                            />
                          }
                          alternative
                          disabled={disabled}
                          checked={value}
                          onBlur={field.onBlur}
                          onChange={event => {
                            form.setFieldValue(
                              'agreedToReceiveExternalPromotions',
                              event.target.checked
                            );
                          }}
                        />
                      );
                    }}
                  />
                );
              }}
            </Field>
            <Field name="postalCode">
              {(props: FieldProps<Values['postalCode']>) => {
                return (
                  <FormGroup
                    name={props.field.name}
                    label={
                      <FormattedMessage {...messages.userFields.postalCode} />
                    }
                    render={formGroupProps => (
                      <Input
                        id={props.field.name}
                        width="8em"
                        autoComplete="postal-code"
                        {...props.field}
                        {...formGroupProps.inputProps}
                      />
                    )}
                    description={
                      <FormattedMessage {...postalCodeForm.description} />
                    }
                  />
                );
              }}
            </Field>
            <Field name="gender">
              {(props: FieldProps<Values['gender']>) => {
                return (
                  <FormGroup
                    name={props.field.name}
                    width="8em"
                    label={<FormattedMessage {...messages.userFields.gender} />}
                    render={() => (
                      <Select
                        id={props.field.name}
                        {...props.field}
                        value={props.field.value || ''}
                        aria-describedby={birthYearAndGenderDescriptionId}
                        autoComplete="sex"
                      >
                        <option value="" />
                        {Object.keys(globalMessages.genders).map(key => {
                          return (
                            <FormattedMessage
                              key={key}
                              {...globalMessages.genders[key]}
                            >
                              {(txt: string) => (
                                <option value={key}>{txt}</option>
                              )}
                            </FormattedMessage>
                          );
                        })}
                      </Select>
                    )}
                  />
                );
              }}
            </Field>
            <Field name="birthYear">
              {(props: FieldProps<Values['birthYear']>) => {
                return (
                  <FormGroup
                    name={props.field.name}
                    label={
                      <FormattedMessage {...messages.userFields.birthYear} />
                    }
                    description={
                      <div id={birthYearAndGenderDescriptionId}>
                        <FormattedMessage
                          id="profile.edit.data_usage"
                          defaultMessage="Your profile helps us understand who are using the service, and why. This is optional."
                        />
                      </div>
                    }
                    render={() => (
                      <Box width="6em">
                        <Input
                          id={props.field.name}
                          autoComplete="bday-year"
                          type="tel"
                          {...props.field}
                          aria-describedby={birthYearAndGenderDescriptionId}
                        />
                      </Box>
                    )}
                  />
                );
              }}
            </Field>
            <Flex column alignItems="center">
              <LoadableButton
                type="submit"
                variant="primary"
                shape="cool"
                loading={loading}
                disabled={!isFormValid({ errors, dirty })}
                responseState={error ? 'error' : 'success'}
              >
                <I18NSave />
              </LoadableButton>
            </Flex>
          </FormikForm>
        );
      }}
    </Formik>
  );
};

export default InnerUserForm;
